/*
 * Copyright (c) Akveo 2019. All Rights Reserved.
 * Licensed under the Single Application / Multi Application License.
 * See LICENSE_SINGLE_APP / LICENSE_MULTI_APP in the 'docs' folder for license information on type of purchased license.
 */
import {register} from "swiper/element/bundle";
import {enableProdMode} from "@angular/core";
import {platformBrowserDynamic} from "@angular/platform-browser-dynamic";
import {loadTranslations} from "@angular/localize";

// NB: AppModule is imported dynamically inside bootstrap() — NOT statically
// here — so its whole module graph evaluates AFTER loadTranslations() runs.
// Module-level `$localize` calls (e.g. the indicator-card captions in
// meteostation-parameters.ts) resolve at import time; a static import would
// evaluate them before translations load and freeze them to the English
// source. The dynamic import keeps them in the correct order.
import {environment} from "./environments/environment";
import {registerLocaleData} from "@angular/common";
import localeUz from "@angular/common/locales/uz";
import localeRu from "@angular/common/locales/ru";
import localeEn from "@angular/common/locales/en";
import {Settings as LuxonSettings} from "luxon";

register();
registerLocaleData(localeUz, "uz");
registerLocaleData(localeRu, "ru");
registerLocaleData(localeEn, "en");

const SUPPORTED = ["en", "ru", "uz"] as const;
type SupportedLang = (typeof SUPPORTED)[number];

function pickLanguage(): SupportedLang {
  // MUST match LanguageService's resolution order exactly, or the UI
  // ($localize, resolved here before bootstrap) and the API language
  // (Accept-Language, set later by LanguageService) disagree — e.g. an
  // English browser would get an English UI but Uzbek pest/disease data.
  // Order: stored → ?lang= → build default ("uz", same as the LOCALE_ID
  // factory in AppModule). We deliberately do NOT fall back to
  // navigator.language: the platform default is Uzbek, and LanguageService's
  // own navigator branch never fires because LOCALE_ID always resolves to a
  // supported code first.
  const stored = typeof localStorage !== "undefined" ? localStorage.getItem("app.language") : null;
  if (stored && (SUPPORTED as readonly string[]).includes(stored)) {
    return stored as SupportedLang;
  }
  const url = new URL(window.location.href);
  const query = (url.searchParams.get("lang") || "").split("-")[0].toLowerCase();
  if ((SUPPORTED as readonly string[]).includes(query)) {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("app.language", query);
    }
    return query as SupportedLang;
  }
  return "uz";
}

const activeLang = pickLanguage();
LuxonSettings.defaultLocale = activeLang === "uz" ? "uz-Latn" : activeLang;

// Reflect the active language on the root element so `<html lang>` is correct
// for accessibility / SEO and for any consumer that reads it (e.g.
// helper.service.ts). It is unset in index.html, so without this it stays "".
if (typeof document !== "undefined") {
  document.documentElement.lang = activeLang;
}

if (environment.production) {
  enableProdMode();
}

// Load $localize translations for the active language BEFORE bootstrap so
// every ``i18n=""`` attribute (and every ``$localize`...`` template-string
// call in .ts files) evaluates against the chosen language on first render.
// The JSON shape produced by ``scripts/build-transloco-jsons.mjs`` is the
// flat ``{messageId: text}`` map that ``loadTranslations`` expects.
async function bootstrap(): Promise<void> {
  if (activeLang !== "en") {
    try {
      const resp = await fetch(`/assets/i18n/${activeLang}.json`, {cache: "no-cache"});
      if (resp.ok) {
        const translations = (await resp.json()) as Record<string, string>;
        loadTranslations(translations);
      } else {
        console.warn(`i18n: /assets/i18n/${activeLang}.json -> HTTP ${resp.status}`);
      }
    } catch (err) {
      console.warn("i18n: failed to load runtime translations", err);
    }
  }
  // Dynamic import: evaluates the AppModule graph (and all its module-level
  // `$localize` captions) only now, after translations are loaded.
  const {AppModule} = await import("./app/app.module");
  await platformBrowserDynamic().bootstrapModule(AppModule);
}

bootstrap().catch((err) => console.error(err));
